/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/pwa/registerServiceWorker.ts
 *
 * Rôle :
 *   Wrapper autour de `virtual:pwa-register` (vite-plugin-pwa). Demande à
 *   l'utilisateur de mettre à jour quand une nouvelle version est dispo,
 *   plutôt que d'imposer un refresh, cohérent avec les bonnes pratiques
 *   d'une PWA installée à l'écran d'accueil iOS / Android.
 *
 * Réutilisable :
 *   Logique 100 % générique. À copier tel quel dans toute autre PWA Vite.
 *
 * Voir : CLAUDE.md §8.2.3 (stratégies SW), §16.5 (config Workbox).
 *
 * @author Mika
 * @since 2026-05-04
 */

import { registerSW } from 'virtual:pwa-register';
import { afficherBandeauMiseAJour } from './updateBanner';
import { rechargerVersionRecente } from './rechargerVersionRecente';
import { activerOuSignaler, demarrerPhaseOuverture, quandReouverture } from './activationOuverture';

/**
 * Filet : si l'activation demandée n'a pas rechargé la page dans ce délai
 * (nouvelle version devenue caduque, message perdu), on affiche le bandeau.
 * Mikael - chef de projet - 2026-10-04
 */
const DELAI_FILET_ACTIVATION_MS = 10 * 1000;

/**
 * Intervalle de re-vérification d'une nouvelle version.
 *
 * Sans cela, le navigateur ne cherche une mise à jour qu'à la NAVIGATION.
 * Or le back-office est une application d'une seule page : on peut y passer
 * des heures sans jamais changer de document, donc sans jamais découvrir
 * qu'une version plus récente existe. Une heure est un compromis : assez
 * fréquent pour qu'un correctif du matin arrive dans la journée, assez rare
 * pour ne peser sur rien.
 */
// 2026-10-04 : 5 minutes (c'était une heure), et retour sur l'onglet après
// une minute, même rythme que versionWatch.ts. Mikael - chef de projet
const INTERVALLE_VERIF_MS = 5 * 60 * 1000;
const DELAI_RETOUR_MS = 60 * 1000;

/**
 * Enregistre le Service Worker, gère les notifications de mise à jour.
 *
 * @returns Une fonction qui force la mise à jour (à brancher sur un bouton
 *          UI plus tard, dans un écran « Réglages »).
 */
export function registerServiceWorker(): () => void {
  // 2026-10-04 (Mika : « l'ancien design reste présent sur mon iPhone même
  // après avoir actualisé ») : on observe dès maintenant les premiers instants
  // de la page, pour savoir si une nouvelle version peut être activée sans
  // rien faire perdre (voir activationOuverture.ts). Mikael - chef de projet
  demarrerPhaseOuverture();
  /** Une nouvelle version attend son activation (signalée par le service worker). */
  let versionEnAttente = false;
  let decisionEnCours = false;
  const bandeau = (): void => {
    // 2026-10-04 : le bouton active la version en attente puis recharge
    // (rechargerVersionRecente), quel que soit le chemin qui a vu la mise à
    // jour. Mikael - chef de projet - 2026-10-04
    // 2026-10-07 : `force` : une version attend, le secours ne repasse
    // jamais par l'ancien service worker. Mikael - chef de projet - 2026-10-07
    afficherBandeauMiseAJour(() => {
      void rechargerVersionRecente(undefined, { force: true });
    });
  };
  const decider = (): void => {
    if (!versionEnAttente || decisionEnCours) return;
    decisionEnCours = true;
    void activerOuSignaler({
      activer: () => {
        // updateSW(true) envoie SKIP_WAITING au service worker en attente ;
        // vite-plugin-pwa recharge la page UNE fois quand il prend la main.
        void updateSW(true);
        window.setTimeout(bandeau, DELAI_FILET_ACTIVATION_MS);
      },
      signaler: bandeau,
    }).finally(() => {
      decisionEnCours = false;
    });
  };
  // Retour au premier plan après une longue absence (application installée
  // sur iPhone, jamais fermée) : si une version attend, on la propose de
  // nouveau à l'activation, aux mêmes conditions.
  quandReouverture(decider);

  // `registerSW` est injecté par vite-plugin-pwa au build.
  // En dev (`devOptions.enabled: false` cf. vite.config.ts), il est no-op.
  const updateSW = registerSW({
    // 🔴 FIX 2026-09-09 (Mika), ce point d'entrée utilisait `window.confirm()`.
    // Une boîte modale bloquante, qui interrompt en plein travail : on la
    // ferme d'un réflexe, et l'on reste sur l'ancienne version SANS aucune
    // trace de ce qu'on vient de refuser. Résultat constaté : une recherche
    // livrée le 30/08 et bien EN LIGNE restait invisible dix jours plus tard.
    // Remplacé par un bandeau non bloquant qui PERSISTE tant qu'on n'a pas
    // agi, l'inverse d'un `confirm()`, bloquant et éphémère.
    // 2026-10-04 : à l'ouverture, sans geste ni saisie et file hors ligne
    // vide, la nouvelle version est activée tout de suite (un seul
    // rechargement, garde anti-boucle). Sinon, le bandeau comme avant.
    onNeedRefresh() {
      versionEnAttente = true;
      decider();
    },
    onOfflineReady() {
      // L'app est désormais utilisable hors-ligne. Pas d'alerte intrusive.
      console.info('[TNE 2026] Application disponible hors ligne.');
    },
    onRegisterError(error) {
      console.error('[TNE 2026] Erreur enregistrement Service Worker :', error);
    },
    onRegisteredSW(_url, registration) {
      // Re-vérification périodique : voir INTERVALLE_VERIF_MS. Le back-office
      // étant une application d'une seule page, sans cela un administrateur
      // resté connecté ne découvrirait JAMAIS une nouvelle version.
      if (registration === undefined) {
        return;
      }
      window.setInterval(() => {
        // `update()` peut échouer (hors ligne, serveur momentanément absent) :
        // c'est sans gravité, on retentera au prochain tour. On ne laisse pas
        // pour autant la promesse se rejeter dans le vide.
        void registration.update().catch(() => undefined);
      }, INTERVALLE_VERIF_MS);
      // 2026-09-20 : aussi au RETOUR sur l'onglet, si la dernière vérification
      // date de plus d'une heure. Un onglet laissé ouvert la veille découvre
      // ainsi la nouvelle version dès qu'on y revient, sans attendre le tour
      // d'horloge. Comme EPS Manageur.
      let derniere = Date.now();
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && Date.now() - derniere >= DELAI_RETOUR_MS) {
          derniere = Date.now();
          void registration.update().catch(() => undefined);
        }
      });
    },
  });

  return () => void updateSW(true);
}
